import {
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack
} from "@mui/material";
import Button from "@mui/material/Button";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import useApiHook from "../hooks/useApiHook";
import { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import VisuallyHiddenInput from "./_atoms/inputs/VisuallyHiddenInput";
import UploadProgress from "./_atoms/UploadProgress";
import ClearIcon from '@mui/icons-material/Clear';
import ErrorIcon from '@mui/icons-material/Error';
import { usePackageModal } from "../store/modal-context";
import { useUser } from "../store/user-context";
import HiddenContentSwitch from "./_atoms/inputs/HiddenContentSwitch";
import { getAudioDuration } from "../utils/audio";

const FileUploadDialog = ({ currentFolderId = null, currentFolderName = null, onFileUpload, onClose, open }) => {
    const { response, makeRequest, loading, uploadProgress, cancelRequest, error: requestError } = useApiHook();
    const [fileName, setFileName] = useState("");
    const [uploadCompleted, setUploadCompleted] = useState(false);
    const [uploadedFileId, setUploadedFileId] = useState(null);
    const [uploadLimitError, setUploadLimitError] = useState(false);
    const packageModalCtx = usePackageModal();
    const userCtx = useUser();
    const hiddenContentSwitchRef = useRef(null);

    useEffect(() => {
        if (open) {
            if (userCtx.hasReachLimit()) return setUploadLimitError(true);
            resetState();
        }
    }, [open]);

    const resetState = () => {
        setUploadCompleted(false);
        setUploadedFileId(null);
        setUploadLimitError(false);
    };

    const uploadBtnClickHandler = async (ev) => {
        const selectedFile = ev.target.files?.[0];
        if (!selectedFile) return;

        setUploadLimitError(false);
        setFileName(selectedFile.name);
        setUploadCompleted(false);
        setUploadedFileId(null);

        try {
            const durationSeconds = await getAudioDuration(selectedFile);
            if (!userCtx.canUploadSeconds(durationSeconds)) {
                setUploadLimitError(true);
                ev.target.value = null;
                return;
            }
        } catch (error) {
            console.warn('Unable to validate audio duration before upload', error);
        }

        makeRequest('/file', 'post', { audioFile: selectedFile });
    };

    useEffect(() => {
        if (response) {
            if (response.state === 'success') {
                if (response.data && response.data.fileId) {
                    setUploadedFileId(response.data.fileId);
                    setUploadCompleted(true);
                }
            } else {
                // TODO: gestire errore
            }
        }
        if (requestError) {
            if (requestError.error?.code === 400) {
                setUploadLimitError(true);
            }
        }
    }, [response, requestError]);

    useEffect(() => {
    }, [uploadProgress]);

    const cancelBtnClickHandler = () => {
        if (uploadedFileId) {
            makeRequest('/file/delete', 'post', { fileId: uploadedFileId });
        } else {
            console.warn('Cancel button clicked but no fileId available');
        }
        setUploadCompleted(false);
        setUploadedFileId(null);
        onFileUpload();
        onClose();
    };

    const transcribeBtnClickHandler = async () => {
        if (uploadedFileId) {
            await makeRequest('/file/transcribe', 'post', {
                fileId: uploadedFileId,
                folderId: currentFolderId,
                hideSensitiveContent: hiddenContentSwitchRef.current
            });
        }
        setUploadCompleted(false);
        setUploadedFileId(null);
        userCtx.fetchUsage();
        onFileUpload();
        onClose();
    };

    const onBuyCreditsBtnClick = () => {
        onClose();
        packageModalCtx.openModal();
    };

    return <>
        <Dialog
            open={open}
            onClose={onClose}
        >
            <Stack direction="row">
                <DialogTitle sx={{ flexGrow: 1 }}>
                    {uploadLimitError ? "Minuti non sufficienti per questo audio" : uploadCompleted ? "File caricato al 100%" : "Nuovo file"}
                </DialogTitle>
                <Button variant="text" color="error" size="small" onClick={onClose}><ClearIcon /></Button>
            </Stack>
            <DialogContent>
                {!uploadLimitError && !loading && !uploadCompleted && <>
                    <DialogContentText>
                        Aggiungi un nuovo file da trascrivere.
                        <br />Il processo di trascrizione iniziera una volta caricato il file.
                    </DialogContentText>
                    {currentFolderId && (
                        <Typography mt={2} textAlign="center" variant="body2" sx={{ opacity: 0.65 }}>
                            Il file verra caricato nella cartella <strong>{currentFolderName}</strong>.
                        </Typography>
                    )}
                    <Stack mt={4} alignItems="center" justifyContent="center">
                        <Button
                            size={"large"}
                            component={'label'}
                            tabIndex={-1}
                            variant="contained"
                            color="primary"
                            startIcon={<FileUploadIcon />}
                        >
                            Seleziona file
                            <VisuallyHiddenInput
                                type="file"
                                accept="audio/mp3,audio/*"
                                onChange={uploadBtnClickHandler}
                            />
                        </Button>
                    </Stack>
                </>}
                {(!uploadLimitError && (loading || uploadCompleted)) && <Stack alignItems="center" justifyContent="center">
                    <Typography variant="body1" mb={2} component="div">
                        {uploadCompleted ? "Caricamento completato!" : "Caricamento file in corso..."}
                    </Typography>
                    <UploadProgress uploadPercent={uploadProgress} />
                    <Typography pt={1} variant="body1">{fileName}</Typography>
                    {uploadCompleted && (
                        <HiddenContentSwitch ref={hiddenContentSwitchRef} />
                    )}
                    <Stack direction="row" spacing={2} mt={2}>
                        <Button variant="contained" color="error" onClick={uploadCompleted ? cancelBtnClickHandler : cancelRequest} startIcon={<ClearIcon />}>
                            Annulla
                        </Button>
                        {uploadCompleted && (
                            <Button variant="contained" color="success" onClick={transcribeBtnClickHandler}>
                                Trascrivi
                            </Button>
                        )}
                    </Stack>
                </Stack>}
                {(uploadLimitError) && <Stack alignItems='center' justifyContent="center">
                    <ErrorIcon sx={{ fontSize: '4rem' }} color={'error'} />
                    <Typography mt={2} color={"error"} variant='h4'>Minuti non sufficienti per questa trascrizione</Typography>
                    <Typography textAlign='center' variant='h6'>Per trascrivere questo audio servono piu minuti di quelli disponibili nel tuo account.</Typography>
                    <Button sx={{ mt: 4 }} variant="contained" size='large' onClick={onBuyCreditsBtnClick}>Acquista nuovi crediti</Button>
                </Stack>}
            </DialogContent>
        </Dialog>
    </>;
};

export default FileUploadDialog;
